import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Clock, ArrowUp, RefreshCw, Eye } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import FAQ from '../../components/user/FAQ';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import blogService, { PublicBlog } from '../../services/user/blogService';
import { getImageUrl } from '../../utils/imageHelper';

const BlogDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [post, setPost] = useState<PublicBlog | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState<PublicBlog[]>([]);
  const [showScroll, setShowScroll] = useState(false);
  const [progress, setProgress] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Extract headings from HTML content
  const extractHeadings = (html: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
    return Array.from(headings).map((heading, index) => {
      const id = heading.id || `heading-${index}`;
      if (!heading.id) {
        heading.id = id;
      }
      return {
        id,
        text: heading.textContent || '',
        level: parseInt(heading.tagName.charAt(1))
      };
    });
  };

  // Estimate reading time
  const estimateReadingTime = (html: string): number => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || '';
    const words = text.split(/\s+/).length;
    const readingSpeed = 200; // words per minute
    return Math.max(1, Math.ceil(words / readingSpeed));
  };

  // Memoize image URL to prevent unnecessary recalculations
  const imageUrl = useMemo(() => {
    if (!post?.image) return null;
    return getImageUrl(post.image) || post.image;
  }, [post?.image]);

  // Extract headings from blog content
  const headings = useMemo(() => {
    if (!post?.content) return [];
    return extractHeadings(post.content);
  }, [post?.content]);

  // Calculate reading time
  const readingTime = useMemo(() => {
    if (!post?.content) return post?.read_time || 5;
    return estimateReadingTime(post.content);
  }, [post?.content, post?.read_time]);

  // Parse tags if it's a string (must be before conditional returns)
  const tags = useMemo((): string[] => {
    if (!post?.tags) return [];
    const tagsValue = post.tags;
    if (typeof tagsValue === 'string') {
      try {
        const parsed = JSON.parse(tagsValue);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return (tagsValue as string).split(',').map((t: string) => t.trim()).filter(Boolean) as string[];
      }
    }
    return Array.isArray(tagsValue) ? tagsValue : [];
  }, [post?.tags]);

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const blog = await blogService.getPublishedBlogById(Number(id));
      setPost(blog);

      // Fetch related posts
      const allBlogs = await blogService.getPublishedBlogs({
        page: 1,
        limit: 100,
        category: blog.category,
      });
      const related = allBlogs.blogs
        .filter(b => b.id !== blog.id && b.category === blog.category)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (error: any) {
      console.error('Failed to fetch blog:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  // Scroll-to-top button and progress tracking
  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);

      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        const winHeight = window.innerHeight;
        const total = rect.height - winHeight;
        const scrolled = Math.min(Math.max(-rect.top, 0), total);
        setProgress(total > 0 ? (scrolled / total) * 100 : 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 px-4 text-center">
          <RefreshCw className="animate-spin text-primary-500 mx-auto mb-4" size={48} />
          <p className="text-secondary-600">Loading blog post...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 px-4 text-center">
          <h1 className="text-4xl font-bold text-secondary-900 mb-4">Post Not Found</h1>
          <p className="text-secondary-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
          >
            <ArrowLeft size={20} />
            <span>Back to Blog</span>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatNumber = (num: number | undefined) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-[#faf8ed] pt-28">
      {/* Progress Bar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: `${progress}%`,
          height: 6,
          background: 'linear-gradient(90deg, #0c513f, #1e8769)',
          zIndex: 9999,
          transition: 'width 0.2s',
          boxShadow: '0 2px 10px rgba(12,81,63,0.3)'
        }}
      />

      <Navbar />

      {/* Enhanced Breadcrumbs */}
      <nav className="bg-white/90 backdrop-blur-xl py-4 px-4 text-sm text-secondary-600 border-b border-[#0c513f]/5 sticky top-[96px] md:top-[112px] z-40 shadow-sm">
        <div className="container-premium">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Back Button */}
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-[#0c513f] hover:text-[#0a4535] font-black transition-all hover:translate-x-[-4px] group uppercase tracking-wide text-xs"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              <span>Back to Blog</span>
            </Link>

            {/* Breadcrumbs */}
            <ol className="flex gap-2 items-center font-medium">
              <li><Link to="/" className="hover:text-[#0c513f] transition-colors">Home</Link></li>
              <li className="text-secondary-300">/</li>
              <li><Link to="/blog" className="hover:text-[#0c513f] transition-colors">Blog</Link></li>
              <li className="text-secondary-300">/</li>
              <li><Link to={`/blog?category=${post.category}`} className="hover:text-[#0c513f] transition-colors text-[#0c513f]">{post.category}</Link></li>
            </ol>
          </div>
        </div>
      </nav>

      {/* Enhanced Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#faf8ed] text-secondary-900 py-12 md:py-16 px-4 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-mesh-premium opacity-50"></div>

        <div className="container-premium text-center relative z-10 transition-all duration-1000">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8 leading-[1.1] text-[#0c513f] tracking-tighter-premium max-w-5xl mx-auto">
            {post.title}
          </h1>

          {/* Enhanced Meta Information */}
          <div className="flex flex-wrap gap-4 justify-center items-center text-sm mb-10">
            <span className="bg-[#0c513f] text-white px-5 py-2 rounded-full font-bold shadow-lg uppercase tracking-wider text-xs">
              {post.category}
            </span>
            <span className="flex items-center gap-2 bg-[#fff9e6] px-5 py-2 rounded-full text-[#0c513f] font-bold border border-yellow-100">
              <Calendar size={16} /> {formatDate(post.published_at || post.created_at)}
            </span>
            <span className="flex items-center gap-2 bg-[#e0f2fe] px-5 py-2 rounded-full text-[#0369a1] font-bold border border-blue-100">
              <Clock size={16} /> {readingTime} min read
            </span>
            {post.views !== undefined && (
              <span className="flex items-center gap-2 bg-[#f0fdf4] px-5 py-2 rounded-full text-[#15803d] font-bold border border-green-100">
                <Eye size={16} /> {formatNumber(post.views)} views
              </span>
            )}
            {Boolean(post.featured) && (
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-5 py-2 rounded-full text-xs font-black shadow-lg uppercase tracking-wider flex items-center gap-1">
                ⭐ Featured
              </span>
            )}
          </div>

          {/* Enhanced Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-10">
              {tags.map((tag: string, i: number) => (
                <span
                  key={i}
                  className="bg-white text-secondary-600 px-4 py-1.5 rounded-full text-sm font-bold border-2 border-secondary-100 hover:border-[#0c513f] hover:text-[#0c513f] transition-all cursor-default"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Author Info */}
          <div className="flex items-center justify-center gap-3 text-secondary-600">
            <div className="p-2 bg-[#0c513f]/10 rounded-full">
              <User size={20} className="text-[#0c513f]" />
            </div>
            <span className="font-bold text-lg text-[#0c513f]">Written by {post.author}</span>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="bg-white py-20 px-4 border-t border-[#0c513f]/5 rounded-t-[3rem] -mt-10 relative z-20 shadow-[-40px_0_40px_rgba(0,0,0,0.03)]">
        <div className="container-premium max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Enhanced Main Content */}
            <div className="flex-1 w-full order-2 lg:order-1">
              {/* Featured Image */}
              <div className="rounded-[2.5rem] overflow-hidden mb-12 border border-[#0c513f]/10 shadow-2xl bg-[#faf8ed] w-full relative group">
                {imageUrl ? (
                  <img
                    key={imageUrl}
                    src={imageUrl}
                    alt={post.title}
                    className="w-full h-auto object-cover min-h-[250px] md:min-h-[400px] max-h-[600px] group-hover:scale-105 transition-transform duration-1000"
                    onError={(e) => {
                      const imgElement = e.currentTarget;
                      imgElement.style.display = 'none';
                      const parent = imgElement.parentElement;
                      if (parent && !parent.querySelector('.error-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'error-placeholder bg-[#faf8ed] p-24 flex items-center justify-center min-h-[400px] w-full';
                        placeholder.innerHTML = '<div class="text-8xl opacity-20">📝</div>';
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                ) : (
                  <div className="bg-[#fff9e6] p-24 flex items-center justify-center min-h-[400px] w-full">
                    <div className="text-8xl opacity-30">📝</div>
                  </div>
                )}
              </div>

              {/* Blog Content */}
              <div className="mb-16" ref={contentRef}>
                <article className="prose prose-xl prose-stone max-w-none 
                  prose-headings:font-black prose-headings:text-[#0c513f] prose-headings:tracking-tight
                  prose-p:text-secondary-600 prose-p:leading-relaxed prose-p:font-medium
                  prose-a:text-[#0c513f] prose-a:font-bold prose-a:no-underline hover:prose-a:underline
                  prose-blockquote:border-l-4 prose-blockquote:border-[#0c513f] prose-blockquote:bg-[#faf8ed] prose-blockquote:py-4 prose-blockquote:px-8 prose-blockquote:rounded-r-xl prose-blockquote:not-italic
                  prose-img:rounded-3xl prose-img:shadow-xl
                  prose-strong:text-[#0c513f] prose-strong:font-black
                ">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: post.content || '<p>No content available.</p>'
                    }}
                  />
                </article>
              </div>

              {/* Back Button */}
              <div className="mt-12 flex justify-center">
                <Link
                  to="/blog"
                  className="bg-[#0c513f] text-white px-10 py-4 font-black text-lg transition-all duration-300 rounded-full hover:bg-[#0a4535] flex items-center gap-3 shadow-xl hover:scale-105 hover:shadow-2xl"
                >
                  <ArrowLeft size={24} /> Back to All Articles
                </Link>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="mt-20 pt-12 border-t border-[#0c513f]/10">
                  <h3 className="text-4xl font-black text-[#0c513f] mb-10 tracking-tight">read Next</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {relatedPosts.map((relatedPost) => {
                      const relatedImageUrl = relatedPost.image ? getImageUrl(relatedPost.image) || relatedPost.image : null;
                      return (
                        <Link
                          key={relatedPost.id}
                          to={`/blog/${relatedPost.id}`}
                          className="bg-white rounded-[2rem] border border-[#0c513f]/5 overflow-hidden hover:border-[#0c513f]/20 hover:shadow-2xl transition-all duration-500 group hover:-translate-y-2 flex flex-col h-full"
                        >
                          <div className="bg-[#faf8ed] h-56 flex items-center justify-center overflow-hidden">
                            {relatedImageUrl ? (
                              <img
                                src={relatedImageUrl}
                                alt={relatedPost.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<div class="text-6xl opacity-20">📝</div>';
                                  }
                                }}
                              />
                            ) : (
                              <div className="text-6xl opacity-20">📝</div>
                            )}
                          </div>
                          <div className="p-8 flex-1 flex flex-col">
                            <h4 className="font-black text-[#0c513f] text-xl mb-3 group-hover:underline decoration-4 transition-all line-clamp-2 leading-tight">
                              {relatedPost.title}
                            </h4>
                            <p className="text-secondary-600 font-medium text-sm mb-6 line-clamp-3 leading-relaxed flex-1">
                              {relatedPost.description}
                            </p>
                            <div className="flex items-center justify-between text-xs font-bold text-secondary-400 uppercase tracking-widest mt-auto">
                              <span>{formatDate(relatedPost.published_at || relatedPost.created_at)}</span>
                              <span>{relatedPost.read_time || 5} min</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Table of Contents - Sidebar */}
            {headings.length > 0 && (
              <aside className="lg:w-80 order-1 lg:order-2">
                <div className="bg-[#faf8ed] rounded-[2rem] p-8 lg:sticky lg:top-32 border border-[#0c513f]/5">
                  <h3 className="text-lg font-black mb-6 text-[#0c513f] flex items-center gap-3 uppercase tracking-wider">
                    <span className="text-2xl">📋</span> Contents
                  </h3>
                  <ul className="space-y-4 text-sm font-medium">
                    {headings.map((h) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className="text-secondary-600 hover:text-[#0c513f] transition-all hover:translate-x-1 block leading-snug"
                        >
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${progress > 10 ? 'bg-[#0c513f]' : 'bg-secondary-300'}`}></span>
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ul>

                  {/* Reading Progress */}
                  <div className="mt-8 pt-8 border-t border-[#0c513f]/10">
                    <div className="text-xs font-bold text-secondary-500 uppercase tracking-widest mb-3">Reading Progress</div>
                    <div className="w-full bg-white rounded-full h-3 shadow-inner">
                      <div
                        className="bg-gradient-to-r from-[#0c513f] to-[#1e8769] h-3 rounded-full transition-all duration-300 shadow-sm"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="text-right text-xs font-bold text-[#0c513f] mt-2">{Math.round(progress)}%</div>
                  </div>
                </div>
              </aside>
            )}

          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="bg-[#faf8ed] py-20 px-4">
        <div className="container-premium max-w-4xl">
          <FAQ
            title="Questions? We've got answers."
            faqs={[
              {
                question: "How does this relate to Rita's services?",
                answer: "This article directly relates to Rita's core services and features. It provides insights into how our platform works and how users can benefit from our payment solutions."
              },
              {
                question: "Can I implement these tips in my daily routine?",
                answer: "Absolutely! All the tips and advice provided in this article are practical and can be easily implemented in your daily routine using Rita's platform."
              },
              {
                question: "Where can I learn more about this topic?",
                answer: "You can explore more related articles in our blog section, check out our detailed guides, or contact our support team for personalized assistance."
              },
              {
                question: "Is this information regularly updated?",
                answer: "Yes, we regularly update our blog content to reflect the latest industry trends, technological advancements, and user feedback to ensure you have the most current information."
              }
            ]}
          />
        </div>
      </section>

      {/* Scroll-to-top Button */}
      {showScroll && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-10 right-6 z-[9999] bg-[#0c513f] text-white p-4 rounded-full shadow-2xl hover:bg-[#0a4535] hover:scale-110 transition-all duration-300 flex items-center justify-center border-4 border-white"
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </button>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetails;
